import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  type SelectChangeEvent,
} from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useUserMetadataStore } from '../../stores/root_store';
import type { MetadataType, UserMetadata } from '../../api/user_metadata_api';

interface UserNotesCardProps {
  userKey: string; // datastore key for the user
  canWork: boolean;
}

export const UserNotesCard = observer(function UserNotesCard({
  userKey,
  canWork,
}: UserNotesCardProps) {
  const store = useUserMetadataStore();
  const [isAdding, setIsAdding] = useState(false);
  const [selectedTypeKey, setSelectedTypeKey] = useState('');
  const [newNoteValue, setNewNoteValue] = useState('');

  // Fetch metadata types and user metadata on mount
  useEffect(() => {
    store.fetchMetadataTypes();
    if (userKey) {
      store.fetchByRef(userKey);
    }
  }, [store, userKey]);

  const metadataTypes = store.getMetadataTypes();
  const userMetadata = store.getByRef(userKey);
  const isLoading = store.isLoadingByRef(userKey) || store.metadataTypesLoading;
  const selectedType = metadataTypes.find((t) => t.key === selectedTypeKey);

  const handleTypeChange = (e: SelectChangeEvent<string>) => {
    setSelectedTypeKey(e.target.value);
    setNewNoteValue('');
  };

  const handleAddNote = async () => {
    if (!selectedTypeKey || !newNoteValue.trim()) return;

    await store.createMetadata({
      ref: userKey,
      metadata_request: selectedTypeKey,
      value: parseValue(newNoteValue, selectedType?.type || 'string'),
    });

    setSelectedTypeKey('');
    setNewNoteValue('');
    setIsAdding(false);
  };

  const handleDeleteNote = async (key: string) => {
    await store.removeMetadata(key, userKey);
  };

  const parseValue = (
    value: string,
    dataType: string
  ): string | number | boolean => {
    switch (dataType) {
      case 'integer':
        return parseInt(value, 10);
      case 'float':
        return parseFloat(value);
      case 'boolean':
        return value === 'true';
      default:
        return value;
    }
  };

  const renderValueInput = (dataType: string) => {
    switch (dataType) {
      case 'text':
        return (
          <TextField
            label="Value"
            value={newNoteValue}
            onChange={(e) => setNewNoteValue(e.target.value)}
            multiline
            rows={3}
            fullWidth
            size="small"
          />
        );
      case 'integer':
        return (
          <TextField
            label="Value"
            value={newNoteValue}
            onChange={(e) => setNewNoteValue(e.target.value)}
            type="number"
            inputProps={{ step: 1 }}
            fullWidth
            size="small"
          />
        );
      case 'float':
        return (
          <TextField
            label="Value"
            value={newNoteValue}
            onChange={(e) => setNewNoteValue(e.target.value)}
            type="number"
            inputProps={{ step: 0.01 }}
            fullWidth
            size="small"
          />
        );
      case 'boolean':
        return (
          <FormControl fullWidth size="small">
            <InputLabel>Value</InputLabel>
            <Select
              value={newNoteValue}
              onChange={(e) => setNewNoteValue(e.target.value)}
              label="Value"
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        );
      case 'date':
        return (
          <TextField
            label="Value"
            value={newNoteValue}
            onChange={(e) => setNewNoteValue(e.target.value)}
            type="date"
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'datetime':
        return (
          <TextField
            label="Value"
            value={newNoteValue}
            onChange={(e) => setNewNoteValue(e.target.value)}
            type="datetime-local"
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        );
      default:
        return (
          <TextField
            label="Value"
            value={newNoteValue}
            onChange={(e) => setNewNoteValue(e.target.value)}
            fullWidth
            size="small"
          />
        );
    }
  };

  const formatNoteValue = (note: UserMetadata): string => {
    const value = note.value;
    if (value === null || value === undefined) return '';

    const dataType = note.metadata_type_type;
    switch (dataType) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'date':
        return new Date(String(value)).toLocaleDateString();
      case 'datetime':
        return new Date(String(value)).toLocaleString();
      default:
        return String(value);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotesIcon color="primary" />
            <Typography variant="h6">User Notes</Typography>
          </Box>
          {canWork && !isAdding && (
            <IconButton size="small" onClick={() => setIsAdding(true)}>
              <AddIcon />
            </IconButton>
          )}
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Add Note Form */}
        {isAdding && canWork && !isLoading && (
          <Box
            sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            <FormControl fullWidth size="small">
              <InputLabel>Note Type</InputLabel>
              <Select
                value={selectedTypeKey}
                onChange={handleTypeChange}
                label="Note Type"
              >
                {metadataTypes.map((type: MetadataType) => (
                  <MenuItem key={type.key} value={type.key}>
                    {type.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedType && renderValueInput(selectedType.type)}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  setIsAdding(false);
                  setSelectedTypeKey('');
                  setNewNoteValue('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleAddNote}
                disabled={!selectedTypeKey || !newNoteValue.trim() || store.creating}
              >
                {store.creating ? 'Adding...' : 'Add'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Notes List */}
        {!isLoading && userMetadata.length > 0 ? (
          <List dense disablePadding>
            {userMetadata.map((note: UserMetadata) => (
              <ListItem key={note.key} disableGutters>
                <ListItemText
                  primary={note.metadata_type}
                  secondary={formatNoteValue(note)}
                  primaryTypographyProps={{
                    variant: 'caption',
                    color: 'text.secondary',
                  }}
                  secondaryTypographyProps={{ variant: 'body2' }}
                />
                {canWork && (
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleDeleteNote(note.key)}
                      disabled={store.removing}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        ) : !isLoading && !isAdding ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            No notes yet
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
});
